<template>
  <!-- iOS has no programmatic install prompt — Safari only installs via
       Share → Add to Home Screen. This sheet walks the user through it. -->
  <Teleport to="body">
    <div class="install-sheet-overlay" @click.self="$emit('close')">
      <div class="install-sheet" role="dialog" aria-modal="true" aria-label="Install app">
        <div class="install-sheet-header">
          <h3>Install Everglance</h3>
          <button
            type="button"
            class="install-sheet-close"
            aria-label="Close"
            @click="$emit('close')"
          >
            <X :size="20" />
          </button>
        </div>

        <ol class="install-steps">
          <li>
            <span class="install-step-icon"><Share :size="18" /></span>
            <span
              >Tap the <strong>Share</strong> button in the browser toolbar (the square with an
              arrow pointing up).</span
            >
          </li>
          <li>
            <span class="install-step-icon"><SquarePlus :size="18" /></span>
            <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
          </li>
          <li>
            <span class="install-step-icon"><Check :size="18" /></span>
            <span
              >Tap <strong>Add</strong>. Everglance then opens full-screen from your home
              screen.</span
            >
          </li>
        </ol>

        <p class="install-sheet-note">
          Don't see the option? Open this page in <strong>Safari</strong> — some iPhone browsers
          can't install apps.
        </p>

        <button type="button" class="install-sheet-done" @click="$emit('close')">Got it</button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { X, Share, SquarePlus, Check } from 'lucide-vue-next'

defineEmits(['close'])
</script>

<style scoped>
.install-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* Bottom sheet on phones — thumb-friendly and the native iOS convention. */
.install-sheet {
  width: 100%;
  max-width: 480px;
  background: var(--bg-secondary, #fff);
  border-radius: 16px 16px 0 0;
  padding: 18px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.25);
}

.install-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.install-sheet-header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: var(--text-primary, #1f2937);
}

.install-sheet-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: var(--bg-tertiary, #f1f3f5);
  color: var(--text-muted, #666);
  cursor: pointer;
}

.install-steps {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  counter-reset: step;
}

.install-steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.92rem;
  line-height: 1.45;
  color: var(--text-secondary, #374151);
}

.install-step-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: var(--primary-bg-subtle, rgba(27, 117, 210, 0.1));
  color: var(--primary-color, #1b75d2);
}

.install-sheet-note {
  margin: 0 0 16px;
  font-size: 0.82rem;
  color: var(--text-muted, #6b7280);
  line-height: 1.4;
}

.install-sheet-done {
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--primary-color, #1b75d2);
  color: var(--text-inverse, #fff);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
}
</style>
